import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { LuSend } from 'react-icons/lu'
import {
  Button,
  FormField,
  Input,
  Label,
  Text,
  useToast,
} from '@asus-common/fe-ui-shadcn'
import { useRequestPasswordReset } from '@/features/auth/hooks'
import { useAuthUiStore } from '@/features/auth/store'
import {
  requestPasswordResetSchema,
  type RequestPasswordResetFormValues,
} from '@/features/auth/model'

export function EmailStep() {
  const { t } = useTranslation(['reset-password', 'auth-common'])
  const { toast } = useToast()
  const { requestPasswordReset } = useRequestPasswordReset()

  const closeRequestPasswordResetModal = useAuthUiStore(
    s => s.closeRequestPasswordResetModal,
  )

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = useForm<RequestPasswordResetFormValues>({
    resolver: zodResolver(requestPasswordResetSchema),
    defaultValues: { email: '' },
  })

  const onSubmit = handleSubmit(async values => {
    try {
      await requestPasswordReset.mutateAsync({ email: values.email })
      toast({
        title: t('reset_password_success_title'),
        description: t('reset_password_success_toast', { email: values.email }),
        status: 'success',
      })
    } catch {
      toast({
        title: t('toast.network_error_title'),
        description: t('auth-common:form.errors.general_error'),
        status: 'error',
      })
    } finally {
      closeRequestPasswordResetModal()
    }
  })

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-8">
      <Text className="block" fontSize="sm" variant="secondary">
        {t('modal.email_subtitle')}
      </Text>

      <Controller
        name="email"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <FormField>
            <Label
              htmlFor="forgotPasswordEmail"
              size="md"
              className="mb-3 block"
            >
              {t('email')}
            </Label>
            <Input
              id="forgotPasswordEmail"
              loading={isSubmitting}
              type="text"
              size="lg"
              autoComplete="email"
              maxLength={100}
              invalid={!!error}
              errorMessage={error?.message ? t(error.message) : undefined}
              {...field}
            />
          </FormField>
        )}
      />

      <div className="flex justify-end">
        <Button
          type="submit"
          variant="solid"
          status="primary"
          size="md"
          loading={isSubmitting}
          disabled={!isValid || isSubmitting}
          endIcon={<LuSend />}
        >
          {t('modal.send_link')}
        </Button>
      </div>
    </form>
  )
}
